import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import configuration from '../../configuration/configuration.js';
import { Skeleton } from '@comeon/ui';

const DEFAULT_SIZES = '(max-width: 375px) 180px, (max-width: 768px) 240px, (max-width: 1028px) 480px, 640w';
// (min-width: 64.25em) 16.666vw, (min-width: 48em) 25vw, (min-width: 20em) 50vw
const BANNER_HACK_SIZES = '640px';
const EMPTY_OBJ = {};

const pictureImageError = e => {
	let errorImg = false;
	const sources = Array.from(e.target.parentElement.children).slice(0, -1);
	const errorSource = sources.find(source => {
		const hasImgTagError = source.srcset !== e.target.src;
		if (hasImgTagError) {
			errorImg = true;
		}
		return source.srcset.includes(e.target.currentSrc);
	});
	if (errorSource) {
		errorSource.srcset = e.target.src;
	}
	if (errorImg) {
		e.target.src = configuration?.casino?.tmpGameIcon?.src;
	}
};

const standardImageError = e => {
	const {
		casino: { tmpGameIcon }
	} = configuration;
	if (tmpGameIcon?.src) {
		e.target.src = tmpGameIcon.src;
		e.target.srcset = tmpGameIcon.srcSet;
	} else {
		e.target.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
	}
};

const Image = ({
	src,
	srcSet,
	sizes = DEFAULT_SIZES,
	alt,
	lazyload,
	smartImage = false,
	pictureElementData,
	loading = 'lazy',
	onLoad = () => {},
	roundCorners = 'top',
	style,
	enableImagePlaceholder = false,
	placeholderProps = {
		borderRadius: ''
	}
}) => {
	const [imageLoaded, setImageLoaded] = useState(false);
	const handleImageLoaded = e => {
		setImageLoaded(true);
		if (onLoad) {
			onLoad(e);
		}
	};
	const sources = useMemo(
		bannerHack =>
			pictureElementData &&
			pictureElementData.map(obj => (
				<source
					key={obj.srcSet}
					srcSet={obj.srcSet}
					sizes={bannerHack ? BANNER_HACK_SIZES : sizes}
					type={obj.type}
				/>
			)),
		[pictureElementData, sizes]
	);

	if (smartImage) {
		return (
			<BannerImage
				alt={alt}
				src={src}
				lazyload={lazyload}
				srcSet={srcSet}
				sizes={BANNER_HACK_SIZES}
				pictureElementData={pictureElementData}
				sources={sources}
				roundCorners={roundCorners}
				loading="lazy"
			/>
		);
	}

	if (pictureElementData) {
		return (
			<>
				<picture className={`u-height-100 u-width-100 ${imageLoaded ? '' : 'u-hide-visiblity'}`}>
					{sources}
					<img
						src={src}
						alt={alt}
						onError={pictureImageError}
						onLoad={handleImageLoaded}
						loading={loading}
						style={style}
						className={`${imageLoaded ? '' : 'u-height-0'}`}
					/>
				</picture>
				{enableImagePlaceholder && !imageLoaded && <Skeleton {...placeholderProps} />}
			</>
		);
	}

	if (srcSet) {
		return (
			<>
				<img
					alt={alt}
					src={src}
					srcSet={srcSet}
					className={`u-b-radius-${roundCorners} ${imageLoaded ? '' : 'u-hide-visiblity u-height-0'}`}
					sizes={sizes}
					onError={standardImageError}
					loading={loading}
					style={style}
					onLoad={handleImageLoaded}
				/>
				{enableImagePlaceholder && !imageLoaded && <Skeleton {...placeholderProps} />}
			</>
		);
	}

	return (
		<>
			<img
				src={src}
				alt={alt}
				onError={standardImageError}
				className={`u-b-radius-${roundCorners} ${imageLoaded ? '' : 'u-hide-visiblity u-height-0'}`}
				loading={lazyload ? 'lazy' : 'eager'}
				onLoad={handleImageLoaded}
				style={style}
			/>
			{enableImagePlaceholder && !imageLoaded && <Skeleton {...placeholderProps} />}
		</>
	);
};

const WIDTH_AUTO = { width: 'auto' };
/**
 * This is a hack made specifically for this ticket: BANJO-10205
 * We need to figure out if the image shown in promotions page are "banner-images",
 * and if so, we need to show only the part on far right of the image.
 */
const BannerImage = ({ alt, src, sizes, pictureElementData, sources, roundCorners = 'top' }) => {
	const frameRef = useRef();
	const imageRef = useRef();
	const [imgStyle, setImgStyle] = useState(WIDTH_AUTO);
	const [isLoaded, setImgLoaded] = useState(false);

	const doSetImgLoaded = useCallback(() => setImgLoaded(true), [setImgLoaded]);
	useEffect(() => {
		if (isLoaded) {
			const handleBannerImage = () => {
				const frameHeight = frameRef.current.clientHeight;
				const frameWidth = frameRef.current.clientWidth;
				const imageHeight = imageRef.current.clientHeight;
				const imageWidth = imageRef.current.clientWidth;
				const frameRatio = frameHeight / frameWidth;
				const x = frameRatio;
				// These equations are copied from the "slide-effect" used on current explore page. See campaign-game.js
				const finalPercentageMedium = -98.8511 * (x * x) + 203.8606 * x - 41.9727;
				const finalPercentageLarge = -156.4978 * (x * x) + 295.7874 * x - 77.7612;

				/* If width is twice (or more) the amount of the height, it is atleast 2:1 (21:9 etc...) aspect ratio
				And thus we will apply transform to the image. If not, leave the image as is and treat is like a normal image. */
				const isBannerAspectRatio = imageWidth >= 2 * imageHeight;

				if (isBannerAspectRatio) {
					// Large images will have a height of above 200, these images needs to be adjusted differentley than Medium/Small images
					if (frameHeight > 200) {
						const transform = `translate3d(-${finalPercentageLarge}%, 0, 0)`;
						return setImgStyle({ transform, width: 'auto' });
					}
					const transform = `translate3d(-${finalPercentageMedium}%, 0, 0)`;
					return setImgStyle({ transform, width: 'auto' });
				}

				setImgStyle(EMPTY_OBJ);
			};

			handleBannerImage();
		}
	}, [isLoaded]);

	if (pictureElementData)
		return (
			<div ref={frameRef} className="u-width-100 u-height-100">
				<picture>
					{sources}
					<img
						alt={alt}
						src={src}
						className={`u-b-radius-${roundCorners}`}
						sizes={sizes}
						ref={imageRef}
						style={imgStyle}
						onLoad={doSetImgLoaded}
						loading="lazy"
					/>
				</picture>
			</div>
		);

	return (
		<div ref={frameRef} className="u-width-100 u-height-100">
			<img
				alt={alt}
				src={src}
				className={`u-b-radius-${roundCorners}`}
				sizes={sizes}
				ref={imageRef}
				style={imgStyle}
				onLoad={doSetImgLoaded}
				loading="lazy"
			/>
		</div>
	);
};

Image.defaultProps = {
	srcSet: undefined,
	lazyload: false,
	roundCorners: 'top',
	style: {},
	pictureElementData: undefined
};

Image.propTypes = {
	src: PropTypes.string.isRequired,
	srcSet: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	alt: PropTypes.string.isRequired,
	lazyload: PropTypes.bool,
	roundCorners: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
	style: PropTypes.objectOf(PropTypes.string),
	pictureElementData: PropTypes.arrayOf(
		PropTypes.shape({
			srcSet: PropTypes.string,
			type: PropTypes.string
		})
	)
};

export default Image;
